import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Target, Zap, Heart, Frown, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPORT_META = {
  AFL: { color: '#E10600', bg: 'bg-red-50', text: 'text-red-700', label: 'AFL' },
  NRL: { color: '#00539F', bg: 'bg-blue-50', text: 'text-blue-700', label: 'NRL' },
  EPL: { color: '#37003C', bg: 'bg-purple-50', text: 'text-purple-800', label: 'EPL' },
  AL: { color: '#F0423E', bg: 'bg-red-50', text: 'text-[#F0423E]', label: 'A-League' },
  WC: { color: '#4338CA', bg: 'bg-indigo-50', text: 'text-indigo-700', label: 'World Cup' },
};

function Stat({ label, value, sub, icon: Icon, tint = '#1A1A1A' }) {
  return (
    <div className="tipster-card px-5 py-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{label}</span>
        {Icon && <Icon className="w-4 h-4" style={{ color: tint }} />}
      </div>
      <div className="text-3xl font-black tabular-nums" style={{ color: tint }} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

function Avatar({ src, name, size = 96 }) {
  const [errored, setErrored] = useState(false);
  const initials = (name || '?')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const showImage = src && !errored;
  return (
    <div
      className="rounded-full bg-gradient-to-br from-[#007AFF] to-[#0F1B3D] text-white flex items-center justify-center font-black shadow-lg ring-4 ring-white overflow-hidden flex-shrink-0"
      style={{ width: size, height: size, fontSize: size / 3 }}
      data-testid="tipster-avatar"
    >
      {showImage ? (
        <img src={src} alt={name} className="w-full h-full object-cover" onError={() => setErrored(true)} />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
}

export default function TipsterProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [uploading, setUploading] = useState(false);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const [p, m] = await Promise.all([
        axios.get(`${API_URL}/api/tipsters/${userId}/profile`, { withCredentials: true }),
        axios.get(`${API_URL}/api/auth/me`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setProfile(p.data);
      setMe(m.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate('/login');
      toast.error(e?.response?.data?.detail || 'Could not load profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProfile(); }, [userId]);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/admin/users/${userId}/avatar`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Avatar updated');
      // bust the browser image cache
      await loadProfile();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F6FA]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }
  if (!profile) return null;

  const { user, totals, per_sport, streaks, competition_wins, round_wins, overall_ranking, favourite_teams, least_favourite_teams } = profile;
  const isAdmin = !!me?.is_admin;

  const avatarSrc = user.avatar_url
    ? `${window.location.origin}${user.avatar_url}?t=${Date.now()}`
    : null;

  return (
    <div className="min-h-screen bg-[#F5F6FA]" data-testid="tipster-profile-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0F1B3D] to-[#1A2B5C] text-white">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <Link to="/leaderboards" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-6" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /> Back to leaderboards
          </Link>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <Avatar src={avatarSrc} name={user.name} />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold uppercase tracking-widest text-[#FFCC00] mb-1">Tipster</div>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight" data-testid="tipster-name">{user.name}</h1>
              <div className="text-sm text-white/70 mt-1">Joined {new Date(user.joined_at).toLocaleDateString()}</div>
            </div>
            {isAdmin && (
              <label className="cursor-pointer inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-sm px-4 py-2 text-sm font-semibold uppercase tracking-wider transition-colors" data-testid="admin-upload-avatar">
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
                  </>
                ) : (
                  <>Upload photo</>
                )}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleAvatarUpload} disabled={uploading} />
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Season totals */}
        <section>
          <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Season 2026</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat
              label="Total points"
              value={(Math.round(totals.points * 100) / 100).toFixed(2)}
              sub={totals.adjustments ? `${totals.tips} tips · incl. ${totals.adjustments > 0 ? '+' : ''}${totals.adjustments} adjustments` : `${totals.tips} tips`}
              icon={Trophy}
              tint="#FFB81C"
            />
            <Stat label="Accuracy" value={`${totals.accuracy}%`} sub={`${totals.correct}/${totals.completed} correct`} icon={Target} tint="#007AFF" />
            <Stat
              label="Overall rank"
              value={overall_ranking?.rank ? `#${overall_ranking.rank}` : '—'}
              sub={overall_ranking?.total_users ? `of ${overall_ranking.total_users} tipsters` : 'No ranking yet'}
              icon={Trophy}
              tint="#37003C"
            />
            <Stat label="Best streak" value={Math.max(...Object.values(streaks).map(s => s.longest || 0), 0)} sub="Consecutive correct" icon={Zap} tint="#E10600" />
          </div>
        </section>

        {/* Competition titles won */}
        <section>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">
            <Trophy className="w-3.5 h-3.5 text-[#FFCC00]" /> Trophy cabinet
          </div>
          {competition_wins.length === 0 ? (
            <div className="tipster-card px-5 py-6 text-sm text-gray-500 italic" data-testid="no-trophies">
              No competition wins yet. All 2026 comps except the World Cup are still in-season.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {competition_wins.map((c) => (
                <div key={c.competition_id} className="tipster-card px-5 py-5 flex items-center gap-4 border-l-4 border-[#FFCC00]" data-testid={`trophy-${c.competition_id}`}>
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FFCC00] to-[#FFB81C] flex items-center justify-center shadow-md flex-shrink-0">
                    <Trophy className="w-6 h-6 text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-black uppercase tracking-widest text-[#FFB81C]">Winner</div>
                    <div className="text-lg font-black tracking-tight text-[#1A1A1A] truncate">{c.title}</div>
                    <div className="text-xs text-gray-500">{c.points} pts</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Per-sport breakdown */}
        <section>
          <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">By sport</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.keys(SPORT_META).filter((s) => per_sport[s] && (per_sport[s].tips > 0 || per_sport[s].points !== 0)).map((s) => {
              const stat = per_sport[s];
              const meta = SPORT_META[s];
              return (
                <div key={s} className="tipster-card px-5 py-5" data-testid={`sport-card-${s}`}>
                  <div className="flex items-center justify-between mb-4">
                    <span
                      className={`inline-block text-[11px] font-black uppercase tracking-widest px-2.5 py-1 rounded-sm ${meta.bg} ${meta.text}`}
                    >
                      {meta.label}
                    </span>
                    <span className="text-xl font-black tabular-nums" style={{ color: meta.color }}>
                      {stat.points} <span className="text-xs font-bold text-gray-500">pts</span>
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div>
                      <div className="text-lg font-black tabular-nums">{stat.accuracy}%</div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">Accuracy</div>
                    </div>
                    <div>
                      <div className="text-lg font-black tabular-nums text-emerald-600">{streaks[s]?.longest || 0}</div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">Best streak</div>
                    </div>
                    <div>
                      <div className="text-lg font-black tabular-nums text-rose-600">{streaks[s]?.worst || 0}</div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">Worst streak</div>
                    </div>
                    <div>
                      <div className="text-lg font-black tabular-nums">{round_wins?.[s] || 0}</div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">Round wins</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Team preferences */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="tipster-card px-5 py-5" data-testid="favourite-teams-card">
            <div className="flex items-center gap-2 mb-4">
              <Heart className="w-4 h-4 text-red-500" />
              <span className="text-xs font-bold uppercase tracking-widest text-gray-500">Favourite teams</span>
            </div>
            {favourite_teams.length === 0 ? (
              <p className="text-sm text-gray-500">Not enough tips yet.</p>
            ) : (
              <div className="space-y-3">
                {favourite_teams.map((t) => (
                  <div key={`${t.sport}-${t.team}`} className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-sm ${SPORT_META[t.sport]?.bg} ${SPORT_META[t.sport]?.text}`}>
                        {t.sport}
                      </span>
                      <div className="min-w-0">
                        <div className="font-bold text-sm truncate">{t.team}</div>
                        <div className="text-xs text-gray-500">Tipped {t.times_tipped}× · {t.accuracy}% win rate</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="tipster-card px-5 py-5" data-testid="least-favourite-teams-card">
            <div className="flex items-center gap-2 mb-4">
              <Frown className="w-4 h-4 text-gray-500" />
              <span className="text-xs font-bold uppercase tracking-widest text-gray-500">Least favourite (picked against)</span>
            </div>
            {least_favourite_teams.length === 0 ? (
              <p className="text-sm text-gray-500">Not enough tips yet.</p>
            ) : (
              <div className="space-y-3">
                {least_favourite_teams.map((t) => (
                  <div key={`${t.sport}-${t.team}`} className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-sm ${SPORT_META[t.sport]?.bg} ${SPORT_META[t.sport]?.text}`}>
                        {t.sport}
                      </span>
                      <div className="min-w-0">
                        <div className="font-bold text-sm truncate">{t.team}</div>
                        <div className="text-xs text-gray-500">Tipped against {t.times_picked_against}×</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
